'use client';

import { createSubscriptionCheckoutSession } from '@/src/actions/stripe';
import { TooltipComponent } from '@/src/components/layout';
import { DialogRightCornerIcons } from '@/src/components/ui/DialogRightCornerIcons';
import { useBoolean } from '@/src/hooks/use-boolean';
import { convertDate } from '@/src/utils/helpers';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { redirect } from 'next/navigation';
import { ISubscribedInfo } from 'types/src/types';

type Props = {
  creatorId: number;
  userId: number;
  stripeAccount?: string;
  creatorName?: string;
  price?: number;
  profileUniqueLink: string;
  isAnonymous?: boolean;
  className?: string;
  isSubscribe?: boolean;
  setIsSubscribe?: (value: boolean) => void;
  isOpenedFromDialog?: boolean;
  isMyProfile: boolean;
  subscribedInfo?: ISubscribedInfo;
  isGoal?: boolean;
  isCreatorPartner: boolean;
};

export const SubscribeStripe = ({
  creatorId,
  userId,
  stripeAccount,
  profileUniqueLink,
  isAnonymous = false,
  className,
  isSubscribe,
  setIsSubscribe,
  creatorName = 'this creator',
  price = 0,
  isOpenedFromDialog = false,
  isMyProfile,
  subscribedInfo,
  isGoal,
  isCreatorPartner,
}: Props) => {
  const isOpenDialog = useBoolean(false);

  const handleClick = () => {
    if (isAnonymous) {
      redirect('/sign-in');
    } else {
      isOpenDialog.onTrue();
    }
  };

  const handleSubscribeClick = async () => {
    if (isAnonymous) {
      redirect('/sign-in');
    }
    if (!stripeAccount) {
      return;
    }
    const { url } = await createSubscriptionCheckoutSession(
      creatorId,
      userId,
      stripeAccount,
      profileUniqueLink,
      isCreatorPartner,
    );

    if (url && setIsSubscribe) {
      window.location.href = url;
      setIsSubscribe(true);
    }
  };

  return (
    <>
      {subscribedInfo?.cancelAtPeriodEnd ? (
        <TooltipComponent
          text={`Your subscription is canceled and will end at the end of the current billing period ${convertDate(subscribedInfo.subscriptionEndDate ?? '')}`}
          className={cn('w-full md:max-h-20 h-fit line-clamp-4', {
            'md:max-w-52': !isGoal,
          })}
        >
          <Button
            size="sm"
            variant="destructive"
            className={`w-[50%] ${className} cursor-not-allowed`}
          >
            Canceled
          </Button>
        </TooltipComponent>
      ) : (
        <Button
          size="sm"
          className={`w-[50%] ${className} disabled:opacity-100 disabled:bg-blue-primary hover:cursor-pointer`}
          onClick={handleClick}
          disabled={
            isMyProfile || subscribedInfo?.cancelAtPeriodEnd || isSubscribe
          }
        >
          {isSubscribe ? 'Subscribed' : 'Subscribe'}
        </Button>
      )}
      {isOpenDialog.value && (
        <div
          className={cn(
            'fixed inset-0 z-60 flex items-center justify-center h-screen w-screen',
            {
              'w-fit h-fit': isOpenedFromDialog,
            },
          )}
        >
          <div className="bg-white p-5 rounded-2xl shadow-md md:w-[540px] w-95% flex flex-col items-center">
            <div className="flex space-between items-center w-full relative">
              <div className="flex flex-col gap-2">
                <h3 className="text-2xl font-semibold p-0">
                  Subscribe to {creatorName}
                </h3>
                <p className="text-black/60 text-sm font-light">
                  Unlock access to all posts from this creator.
                </p>
              </div>
              <DialogRightCornerIcons
                leftIcon="unlock"
                rightIcon="flash-icon"
                className="absolute top-0 right-0"
              />
            </div>
            <div className="font-semibold text-[40px] pt-5 flex items-center">
              ${price}
              <span className="font-light text-sm text-black/60 pl-2">
                /month
              </span>
            </div>
            <hr className="w-full border-gray-100 my-5" />
            <div className="w-full flex justify-between">
              <Button
                size="sm"
                variant="outline"
                className="bg-transparent px-4 h-7"
                onClick={isOpenDialog.onFalse}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="px-4 h-7"
                onClick={handleSubscribeClick}
              >
                Subscribe for ${price}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
